import isEmpty from 'lodash/isEmpty';
import { useMemo } from 'react';
import { useGetMembersFromCriteria } from '../../../../hooks/useMembersSearch';
import useFlowBuilderStore from '../../../../stores/flowBuilderStore';
import { blockDataSelector } from '../../../../stores/flowBuilderStore/selectors';
import {
  mapRulesFromCriteriaGroups,
  mapRulesFromCriteriaGroupsAndReturnMembersOnly,
} from '../../../../Utils/flows/criteria';
import { useGetFinalizedMembersFromCriteriaForPreview } from '../../../../hooks/useGetMembersFromCriteriaForPreview';
import {
  getCriteriaPayloadForParticipation,
  getCriteriaPayloadForVisibility,
} from '../../../../Utils/flows/sharesheet';
import { GetMembersForPreviewRequest } from '../../../../interfaces/Sharesheet';
import { useParams } from 'react-router-dom';
import { useFetchFlowDetailsQuery } from '../../../../queries/Flows/Feed';
import { VisibilityTypes } from '../types';

export const useFetchMembersBasedOnFlowCriteria = ({
  enableFetch,
  isMembersOnly,
}: {
  enableFetch: boolean;
  isMembersOnly?: boolean;
}) => {
  const { flowId } = useParams<{ flowId: string }>();
  const { data: flowBuilderData } = useFetchFlowDetailsQuery(flowId, 'builder');

  const blockData = useFlowBuilderStore(blockDataSelector);

  const visibilityBlockData = blockData.VISIBILITY;
  const participantsBlockData = blockData.PARTICIPANTS;

  const { participantsCriteria } = participantsBlockData;
  // @ts-ignore
  const { criteriaGroups: visibilityCriteria } = visibilityBlockData;

  const participantRulesFromCriteria = useMemo(() => {
    const inputCriteria = isMembersOnly
      ? mapRulesFromCriteriaGroupsAndReturnMembersOnly(
          participantsCriteria,
          undefined,
          true,
        )
      : mapRulesFromCriteriaGroups(participantsCriteria, undefined, true);
    return enableFetch && !isEmpty(participantsCriteria)
      ? inputCriteria
      : undefined;
  }, [enableFetch, isMembersOnly, participantsCriteria]);

  const visibilityRulesFromCriteria = useMemo(() => {
    const inputCriteria = isMembersOnly
      ? mapRulesFromCriteriaGroupsAndReturnMembersOnly(
          visibilityCriteria,
          undefined,
          true,
        )
      : mapRulesFromCriteriaGroups(visibilityCriteria, undefined, true);
    return enableFetch ? inputCriteria : undefined;
  }, [isMembersOnly, visibilityCriteria, enableFetch]);

  const {
    data: participantsSelected,
    isLoading: isParticipantsCriteriaLoading,
    totalMembersCount: totalParticipantsCount,
  } = useGetFinalizedMembersFromCriteriaForPreview({
    ...getCriteriaPayloadForParticipation(
      participantRulesFromCriteria?.criteria,
      flowBuilderData,
    ),
  } as GetMembersForPreviewRequest);

  const {
    models: {
      isLoading: isEveryoneCriteriaBeingFetched,
      totalMembers: membersCountForEveryoneCriteria,
    },
  } = useGetMembersFromCriteria(
    {
      limit: 20,
      criteria: { everyone: true },
      isNewMembersAdded: true,
      skippedMembers: [],
    },
    undefined,
    {
      isNewMembersAdded: true,
    },
  );

  const {
    data: membersWithVisibility,
    isLoading: isViewerCriteriaLoading,
    totalMembersCount: totalViewersCount,
  } = useGetFinalizedMembersFromCriteriaForPreview(
    {
      ...getCriteriaPayloadForVisibility(
        visibilityBlockData?.type === VisibilityTypes.PARTICIPANTS_ONLY
          ? participantRulesFromCriteria?.criteria
          : visibilityRulesFromCriteria?.criteria,
        flowBuilderData,
        visibilityBlockData?.type as VisibilityTypes,
      ),
    } as GetMembersForPreviewRequest,
    100,
    !isEveryoneCriteriaBeingFetched && !isParticipantsCriteriaLoading,
  );

  return {
    participantsSelected,
    isParticipantsCriteriaLoading,
    isEveryoneCriteriaBeingFetched,
    totalParticipantsCount,
    isViewerCriteriaLoading,
    membersWithVisibility,
    participantsCriteria,
    visibilityCriteria,
    totalViewersCount,
    visibilityBlockData,
    participantsBlockData,
    membersCountForEveryoneCriteria,
  };
};
